import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useAutoSave — hook de autoguardado con debounce + flush en blur + flush en beforeunload.
 *
 * @param {string} value          Texto actual a guardar.
 * @param {(v:string)=>Promise}   saveFn Función async que persiste el valor.
 * @param {object} opts
 *   - debounceMs (default 8000)  Espera entre el último cambio y el envío.
 *   - enabled (default true)     Si false, no hace nada (útil cuando no hay draft.id).
 *
 * Retorna:
 *   - status: 'idle' | 'saving' | 'saved' | 'error'
 *   - lastSavedAt: Date | null
 *   - flush: ()=>Promise  (fuerza el guardado inmediato)
 */
export default function useAutoSave(value, saveFn, opts = {}) {
    const { debounceMs = 8000, enabled = true } = opts;
    const [status, setStatus] = useState('idle');
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const timerRef = useRef(null);
    const lastSavedValueRef = useRef(value);
    const inflightRef = useRef(false);

    const doSave = useCallback(async () => {
        if (!enabled || inflightRef.current) return;
        if (value == null || value === lastSavedValueRef.current) return;
        inflightRef.current = true;
        setStatus('saving');
        try {
            await saveFn(value);
            lastSavedValueRef.current = value;
            setLastSavedAt(new Date());
            setStatus('saved');
        } catch (_) {
            setStatus('error');
        } finally {
            inflightRef.current = false;
        }
    }, [value, saveFn, enabled]);

    // Debounce: cada cambio reinicia el timer
    useEffect(() => {
        if (!enabled) return undefined;
        if (value === lastSavedValueRef.current) return undefined;
        if (timerRef.current) clearTimeout(timerRef.current);
        setStatus('idle');
        timerRef.current = setTimeout(() => { doSave(); }, debounceMs);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [value, debounceMs, doSave, enabled]);

    // Flush en blur del window (cambio de pestaña) y en beforeunload (cierre)
    useEffect(() => {
        if (!enabled) return undefined;
        const onBlur = () => {
            if (value !== lastSavedValueRef.current && timerRef.current) {
                clearTimeout(timerRef.current);
                doSave();
            }
        };
        const onBeforeUnload = () => {
            // best-effort: el navegador NO espera promesas en beforeunload.
            // Usamos sendBeacon a un endpoint dummy si el draft está dirty; el
            // último valor confiable es el último auto-save de los 8s previos.
            if (value !== lastSavedValueRef.current) {
                // No podemos hacer await aquí; el save ya está programado.
                doSave();
            }
        };
        window.addEventListener('blur', onBlur);
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [doSave, value, enabled]);

    return { status, lastSavedAt, flush: doSave };
}
